import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Snackbar,
  Checkbox,
  FormControlLabel,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import AccountPlanService from '../../services/accountPlan.service';
import { subscriptionStatusColor } from './AccountUtils';
import dayjs from 'dayjs';

const AccountPlan = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [accountPlan, setAccountPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedSpecs, setEditedSpecs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    AccountPlanService.getFeatures()
      .then((res) => setFeatures(res.data.features))
      .catch(() => setError('Failed to load features catalog'))
      .finally(() => setFeaturesLoading(false));
  }, []);

  useEffect(() => {
    if (!featuresLoading) {
      setLoading(true);
      setError(null);
      setAccountPlan(null);
      AccountPlanService.getAccountPlan(accountId)
        .then((res) => setAccountPlan(res.data.accountPlan))
        .catch(() => setError('Failed to load account plan'))
        .finally(() => setLoading(false));
    }
  }, [accountId, featuresLoading]);

  const handleEnterEditMode = () => {
    const specsByFeatureId = {};
    (accountPlan.specs || []).forEach((s) => {
      specsByFeatureId[s.featureId] = s;
    });
    setEditedSpecs(
      features.map((f) => ({
        featureId: f.id,
        enabled: specsByFeatureId[f.id]?.enabled ?? false,
        volumeLimits: { ...(specsByFeatureId[f.id]?.volumeLimits || {}) },
        rules: { ...(specsByFeatureId[f.id]?.rules || {}) }
      }))
    );
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedSpecs([]);
  };

  const handleToggleEnabled = (featureId) => {
    setEditedSpecs((prev) => prev.map((s) => (s.featureId === featureId ? { ...s, enabled: !s.enabled } : s)));
  };

  const handleVolumeLimitChange = (featureId, key, value) => {
    setEditedSpecs((prev) =>
      prev.map((s) =>
        s.featureId === featureId ? { ...s, volumeLimits: { ...s.volumeLimits, [key]: value === '' ? '' : Number(value) } } : s
      )
    );
  };

  const handleRuleChange = (featureId, key, checked) => {
    setEditedSpecs((prev) => prev.map((s) => (s.featureId === featureId ? { ...s, rules: { ...s.rules, [key]: checked } } : s)));
  };

  const handleSave = () => {
    setSaving(true);
    setError(null);
    AccountPlanService.updateSpecs(accountPlan.accountId, editedSpecs)
      .then((res) => {
        setAccountPlan(res.data.accountPlan);
        setEditMode(false);
        setEditedSpecs([]);
        setSuccessMsg('Specs saved successfully');
      })
      .catch(() => setError('Failed to save specs'))
      .finally(() => setSaving(false));
  };

  const handleSyncStripe = () => {
    setSyncing(true);
    setError(null);
    AccountPlanService.syncStripe(accountPlan.accountId)
      .then((res) => {
        const { result, accountPlan: updated } = res.data;
        if (updated) setAccountPlan(updated);
        if (!result?.synced) {
          const why =
            result?.reason === 'no_stripe_customer'
              ? 'This account has no Stripe customer yet.'
              : 'Stripe has no subscriptions for this customer.';
          setError(`Nothing to sync. ${why}`);
          return;
        }
        setSuccessMsg(
          result.previousStatus === result.status
            ? `Already in sync with Stripe (${result.status}).`
            : `Synced with Stripe: ${result.previousStatus} → ${result.status}.`
        );
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to sync with Stripe'))
      .finally(() => setSyncing(false));
  };

  const getSpecForFeature = (featureId) => (accountPlan?.specs || []).find((s) => s.featureId === featureId);

  const renderVolumeLimitsReadOnly = (spec) => {
    if (!spec?.volumeLimits || Object.keys(spec.volumeLimits).length === 0) return '—';
    return Object.entries(spec.volumeLimits)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  };

  const renderRulesReadOnly = (spec) => {
    if (!spec?.rules || Object.keys(spec.rules).length === 0) return '—';
    return Object.entries(spec.rules)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(', ');
  };

  if (loading || featuresLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/accounts/${accountId}`)}>
          Back to Profile
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom>
        Account Plan
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {accountPlan && (
        <>
          {/* Plan Info */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Plan Info</Typography>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncStripe}
                disabled={syncing || editMode}
                title="Read the subscription from Stripe and update the status here"
              >
                Sync with Stripe
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Typography variant="body1">
                <strong>Plan:</strong> {accountPlan.plan?.name ?? '—'}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong>{' '}
                <Chip label={accountPlan.subscriptionStatus} size="small" color={subscriptionStatusColor(accountPlan.subscriptionStatus)} />
              </Typography>
              <Typography variant="body1">
                <strong>Interval:</strong> {accountPlan.subscriptionInterval ?? '—'}
              </Typography>
              <Typography variant="body1">
                <strong>Period End:</strong>{' '}
                {accountPlan.currentPeriodEnd ? dayjs(accountPlan.currentPeriodEnd).format('MMM D, YYYY') : '—'}
              </Typography>
              <Typography variant="body1">
                <strong>Price:</strong> {accountPlan.plan?.price != null ? `$${accountPlan.plan.price}` : '—'}
              </Typography>
              <Typography variant="body1">
                <strong>Custom Plan:</strong>{' '}
                <Chip label={accountPlan.customPlan ? 'Yes' : 'No'} size="small" color={accountPlan.customPlan ? 'warning' : 'default'} />
              </Typography>
              {(accountPlan.subscriptionStatus === 'canceled' ||
                accountPlan.cancellationRequestDate ||
                accountPlan.cancellationReason ||
                accountPlan.reason) && (
                <>
                  <Typography variant="body1">
                    <strong>Cancellation Requested:</strong>{' '}
                    {accountPlan.cancellationRequestDate ? dayjs(accountPlan.cancellationRequestDate).format('MMM D, YYYY') : '—'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Effective:</strong>{' '}
                    {accountPlan.cancellationDate ? dayjs(accountPlan.cancellationDate).format('MMM D, YYYY') : '—'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Reason:</strong> {accountPlan.reason ?? accountPlan.cancellationReason ?? '—'}
                  </Typography>
                  {accountPlan.reason && accountPlan.cancellationReason && (
                    <Typography variant="body1">
                      <strong>Stripe reason:</strong> {accountPlan.cancellationReason}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>

          {/* Specs */}
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Feature Specs</Typography>
              {!editMode ? (
                <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEnterEditMode}>
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Feature</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Enabled</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Volume Limits</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Rules</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => {
                  if (editMode) {
                    const editedSpec = editedSpecs.find((s) => s.featureId === feature.id);
                    if (!editedSpec) return null;
                    return (
                      <TableRow key={feature.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {feature.name}
                          </Typography>
                          {feature.description && (
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Switch checked={editedSpec.enabled} onChange={() => handleToggleEnabled(feature.id)} size="small" />
                        </TableCell>
                        <TableCell>
                          {feature.volumeLimitKeys?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {feature.volumeLimitKeys.map((key) => (
                                <TextField
                                  key={key}
                                  label={key}
                                  type="number"
                                  size="small"
                                  value={editedSpec.volumeLimits[key] ?? ''}
                                  onChange={(e) => handleVolumeLimitChange(feature.id, key, e.target.value)}
                                  sx={{ width: 160 }}
                                  disabled={!editedSpec.enabled}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {feature.ruleKeys?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {feature.ruleKeys.map((key) => (
                                <FormControlLabel
                                  key={key}
                                  label={key}
                                  control={
                                    <Checkbox
                                      checked={editedSpec.rules[key] ?? false}
                                      onChange={(e) => handleRuleChange(feature.id, key, e.target.checked)}
                                      size="small"
                                      disabled={!editedSpec.enabled}
                                    />
                                  }
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const spec = getSpecForFeature(feature.id);
                  return (
                    <TableRow key={feature.id} sx={{ opacity: spec ? 1 : 0.45 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {feature.name}
                        </Typography>
                        {feature.description && (
                          <Typography variant="caption" color="text.secondary">
                            {feature.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch checked={spec?.enabled ?? false} disabled size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{renderVolumeLimitsReadOnly(spec)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{renderRulesReadOnly(spec)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Snackbar open={Boolean(successMsg)} autoHideDuration={4000} onClose={() => setSuccessMsg(null)}>
        <Alert severity="success" variant="filled" onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountPlan;
